// Core
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, DateRangePopover, PageHead, Tabs } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import UploadHelpButton from "components/UploadHelpButton";
import Analytics from "pages/ManageDSO/Analytics";
import ListDso from "pages/ManageDSO/List";
import ListDsoAggregator from "pages/ManageDSO/ListAggregator";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectTimeSelector, updateTimeSelector } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useAddNewDsoMutation } from "state/dso/dsoApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

const TASK_TYPE = "DSO";
const hrefTemplate = "/docs/template_dso.sgl_v2";

function ManageDso() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["dso"]);

  const { startDate, endDate, horizon, isExpanded } = useSelector(selectTimeSelector);

  const [tab, setTab] = useState("charts");
  const fileInputRef = useRef(null);

  const activeLoadsTasks = useSelector(selectActiveTasksByType(TASK_TYPE));

  // CREATE DSO
  const [addDso] = useAddNewDsoMutation();
  const handleDsoFileUpload = handleFileUpload(addDso, community.id, dispatch, TASK_TYPE);

  // GET Task Status
  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeLoadsTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeLoadsTasks.length === 0,
    }
  );

  useTaskStatusEffect({ isSuccess, isFetching, taskStatus, activeLoadsTasks, dispatch, TASK_TYPE });

  const isUploading = activeLoadsTasks.length > 0;

  const tabs = [
    { id: "charts", icon: "show_chart", label: t("tabs.charts") },
    { id: "table", icon: "table_rows", label: t("tabs.table") },
    { id: "agg", icon: "summarize", label: t("tabs.aggregator_table") },
  ];

  return (
    <div>
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("page.title") }]} />
      <PageHead
        title={t("page.title")}
        subtitle={t("page.subtitle")}
        actions={
          <div className="flex items-center gap-[10px] flex-wrap">
            <DateRangePopover
              value={{ start: startDate, end: endDate }}
              onChange={(v) =>
                dispatch(
                  updateTimeSelector({
                    startDate: v.start,
                    endDate: v.end,
                    horizon,
                    isExpanded,
                  })
                )
              }
            />
            <span className="w-px self-stretch bg-border my-1 mx-[2px]" />
            {isUploading && (
              <Chip tone="amber">
                <Icon name="hourglass_top" style={{ fontSize: 13 }} />
                {t("actions.uploading")}
              </Chip>
            )}
            <Button variant="ghost" onClick={() => window.open(hrefTemplate)}>
              <Icon name="download" />
              {t("actions.template")}
            </Button>
            <UploadHelpButton size="md" />
            <Button
              variant="ghost"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="upload_file" />
              {t("actions.upload_ord")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleDsoFileUpload}
            />
          </div>
        }
      />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "charts" && <Analytics />}
      {tab === "table" && <ListDso />}
      {tab === "agg" && <ListDsoAggregator />}
    </div>
  );
}

export default ManageDso;
